/**
 * NotificationsChip — live feed of approvals, decisions, and jobs.
 *
 * Polls /notifications/unread-count every 30s for the badge. Opens
 * ReviewInbox in a popover for the full list + inline approve/reject.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, IconButton, Popover, Tooltip } from '@mui/material';
import { NotificationsNone } from '@mui/icons-material';
import { api } from '../../../services/api';
import ReviewInbox from '../../review/ReviewInbox';

const POLL_MS = 30_000;

const NotificationsChip = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [count, setCount] = useState(0);
  const btnRef = React.useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.getUnreadNotificationCount();
      const next = Number(res?.data?.count);
      setCount(Number.isFinite(next) ? next : 0);
    } catch {
      // Endpoint may not be up yet — fail silent so the chip never breaks the bar.
      setCount(0);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // Records → Approvals Inbox (menubar) opens this popover without a click,
  // anchoring to the bell button.
  useEffect(() => {
    const open = () => { if (btnRef.current) setAnchorEl(btnRef.current); };
    window.addEventListener('eis:open-approvals', open);
    return () => window.removeEventListener('eis:open-approvals', open);
  }, []);

  const close = () => {
    setAnchorEl(null);
    // Refresh the badge — the popover already marked things as read on close.
    setTimeout(refresh, 250);
  };

  return (
    <>
      <Tooltip title={count ? `${count} unread` : 'Notifications'}>
        <IconButton ref={btnRef} size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <Badge badgeContent={count} color="error" max={99}>
            <NotificationsNone sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ReviewInbox onClose={close} />
      </Popover>
    </>
  );
};

export default NotificationsChip;
